import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Button,
  Alert,
  Card,
  Table,
  Spinner,
  Form,
  Dropdown,
} from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { sampleService, type Sample } from '../services/sample.service';
import { excelExportService } from '../services/excelExport.service';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import { SamplePanelCardHeader } from '../components/samples/SamplePanelCardHeader';
import SampleListPaginationBar from '../components/samples/SampleListPaginationBar';
import SampleNeedsStorageSetupAlert from '../components/samples/SampleNeedsStorageSetupAlert';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import { deliverBlobToUser } from '../utils/downloadBlob';
import { fetchMatchingSampleIds } from '../utils/fetchMatchingSampleIds';
import { formatSampleDateTime } from '../utils/dateFnsLocale';
import { formatSampleTypeDisplay } from '../utils/sampleTypeLabels';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import { translateAxiosErrorMessage } from '../utils/error.utils';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { sampleSelectLabels } from '../utils/sampleSelectLabels';
import TextContentSep from '../components/common/TextContentSep';
import { useRedirectIfSampleLocalPreferred } from '../hooks/useRedirectIfSampleLocalPreferred';
import { useSampleStorageSetupGate } from '../hooks/useSampleStorageSetupGate';

const DEFAULT_PAGE_LIMIT = 50;

export default function ExcelExport() {
  const { t, i18n } = useTranslation();
  useRedirectIfSampleLocalPreferred();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);
  const [listLimit, setListLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<number>>(() => new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [selectingAllMatching, setSelectingAllMatching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectionCardRef = useRef<HTMLDivElement>(null);
  /** sticky 選択帯の高さ。テーブル thead の top と同期して見出しが帯の下に潜らないようにする */
  const [selectionStickyPx, setSelectionStickyPx] = useState(0);

  useLayoutEffect(() => {
    const el = selectionCardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      setSelectionStickyPx(Math.ceil(el.getBoundingClientRect().height));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, adminViewAll, listLimit]);

  useEffect(() => {
    setSelectedSampleIds(new Set());
  }, [adminViewAll]);

  const { data, isLoading, isError, isFetching, refetch, error: listError } = useQuery({
    queryKey: ['samples', 'export', adminViewAll, page, listLimit, debouncedSearch],
    queryFn: () =>
      sampleService.list({
        page,
        limit: listLimit,
        search: debouncedSearch || undefined,
        adminViewAll: adminViewAll || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const listErrorMessage =
    listError != null
      ? translateAxiosErrorMessage(
          listError,
          t,
          t('excel_export.load_failed', { defaultValue: 'Failed to load sample data.' })
        )
      : '';

  const samples: Sample[] = data?.data ?? [];
  const pagination = data?.pagination ?? {
    page: 1,
    limit: listLimit,
    total: 0,
    totalPages: 0,
  };

  const pageIds = useMemo(() => samples.map((s) => s.id), [samples]);
  const pageSelectedCount = pageIds.filter((id) => selectedSampleIds.has(id)).length;
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = somePageSelected;
  }, [somePageSelected, allPageSelected]);

  const selectedCountLabel = t('excel_export.selected_of_total', {
    defaultValue: '{{selected}} / {{total}} selected',
    selected: selectedSampleIds.size,
    total: pagination.total,
  });

  const selectPage = () => {
    setSelectedSampleIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) next.add(id);
      return next;
    });
  };

  const deselectPage = () => {
    setSelectedSampleIds((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) next.delete(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedSampleIds(new Set());
  };

  const allMatchingSelected =
    pagination.total > 0 && selectedSampleIds.size >= pagination.total;
  const showSelectAllMatching = pagination.total > pageIds.length;

  const selectAllMatching = async () => {
    if (pagination.total <= 0 || selectingAllMatching) return;
    setSelectingAllMatching(true);
    setError('');
    try {
      const ids = await fetchMatchingSampleIds({
        total: pagination.total,
        search: debouncedSearch || undefined,
        adminViewAll: adminViewAll || undefined,
      });
      setSelectedSampleIds(new Set(ids));
    } catch (err: unknown) {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('excel_export.select_all_matching_failed', {
            defaultValue: 'Failed to select all matching samples.',
          })
        )
      );
    } finally {
      setSelectingAllMatching(false);
    }
  };

  // label/aria = short、title = full（無効時は理由を title に）
  const selectionActions: Array<{
    key: string;
    label: string;
    title: string;
    onClick: () => void;
    disabled: boolean;
    disabledReason?: string;
    variant: 'outline-secondary' | 'outline-primary';
    busy?: boolean;
  }> = [
    {
      key: 'select-page',
      label: t('excel_export.select_page_short', {
        defaultValue: t('excel_export.select_page', { defaultValue: 'Page' }),
      }),
      title: t('excel_export.select_page', { defaultValue: 'Select this page' }),
      onClick: selectPage,
      disabled: pageIds.length === 0 || selectingAllMatching,
      disabledReason:
        pageIds.length === 0
          ? t('excel_export.no_samples', { defaultValue: 'No samples to export' })
          : undefined,
      variant: 'outline-secondary',
    },
    ...(showSelectAllMatching
      ? [
          {
            key: 'select-all-matching',
            label: selectingAllMatching
              ? t('excel_export.selecting_all_matching', {
                  defaultValue: 'Selecting…',
                })
              : t('excel_export.select_all_matching_short', {
                  defaultValue: t('excel_export.select_all_matching', {
                    defaultValue: 'All ({{count}})',
                    count: pagination.total,
                  }),
                  count: pagination.total,
                }),
            title: t('excel_export.select_all_matching', {
              defaultValue: 'Select all matching ({{count}})',
              count: pagination.total,
            }),
            onClick: () => void selectAllMatching(),
            disabled:
              pagination.total === 0 || selectingAllMatching || allMatchingSelected,
            disabledReason:
              pagination.total === 0
                ? t('excel_export.no_samples', { defaultValue: 'No samples to export' })
                : allMatchingSelected
                  ? t('excel_export.all_matching_already_selected', {
                      defaultValue: 'All matching samples are already selected',
                    })
                  : undefined,
            variant: 'outline-primary' as const,
            busy: selectingAllMatching,
          },
        ]
      : []),
    {
      key: 'deselect-page',
      label: t('excel_export.deselect_page_short', {
        defaultValue: t('excel_export.deselect_page', { defaultValue: 'Uncheck page' }),
      }),
      title: t('excel_export.deselect_page', { defaultValue: 'Deselect this page' }),
      onClick: deselectPage,
      disabled: pageSelectedCount === 0 || selectingAllMatching,
      disabledReason:
        pageSelectedCount === 0
          ? t('excel_export.need_page_selection', {
              defaultValue: 'No samples selected on this page',
            })
          : undefined,
      variant: 'outline-secondary',
    },
    {
      key: 'clear',
      label: t('excel_export.clear_selection_short', {
        defaultValue: t('excel_export.clear_selection', { defaultValue: 'Clear' }),
      }),
      title: t('excel_export.clear_selection', { defaultValue: 'Clear selection' }),
      onClick: clearSelection,
      disabled: selectedSampleIds.size === 0 || selectingAllMatching,
      disabledReason:
        selectedSampleIds.size === 0
          ? t('excel_export.select_samples', {
              defaultValue: 'Please select samples to export.',
            })
          : undefined,
      variant: 'outline-secondary',
    },
  ];

  const exportDisabledReason =
    selectedSampleIds.size === 0
      ? t('excel_export.select_samples', {
          defaultValue: 'Please select samples to export.',
        })
      : undefined;
  const handleToggleSample = (sampleId: number) => {
    setSelectedSampleIds((prev) => {
      const next = new Set(prev);
      if (next.has(sampleId)) next.delete(sampleId);
      else next.add(sampleId);
      return next;
    });
  };

  const handleExport = async () => {
    if (selectedSampleIds.size === 0) {
      setError(
        t('excel_export.select_samples', { defaultValue: 'Please select samples to export.' })
      );
      return;
    }

    const confirmMessage = t('excel_export.export_confirm', {
      defaultValue:
        'Export {{count}} sample(s) in Excel template format?\n\nExported files can be imported again using Excel import.',
      count: selectedSampleIds.size,
    });

    if (!window.confirm(confirmMessage)) return;

    setIsExporting(true);
    setError('');
    setSuccess('');

    try {
      const blob = await excelExportService.exportSamples({
        sampleIds: Array.from(selectedSampleIds),
        adminViewAll: adminViewAll || undefined,
      });
      deliverBlobToUser(blob, `samples_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      setSuccess(t('excel_export.export_success', { defaultValue: 'Export completed.' }));
      setSelectedSampleIds(new Set());
    } catch (err: unknown) {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('excel_export.export_failed', { defaultValue: 'Export failed.' })
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container
      fluid
      className="mt-2 px-3 px-md-4"
      id="excel-export-page"
      style={
        {
          ['--excel-export-sticky-top']: `${selectionStickyPx}px`,
        } as CSSProperties
      }
    >
      <SampleManagerNavigation />
      {/* ナビ「入出力」下の副画面。可視 h1 は縦を奪うため sr-only。ヘルプ／管理者は1行へ */}
      <SampleManagerPageHeading
        shortLabel={t('excel_export.nav_short', { defaultValue: 'Export' })}
        fullLabel={t('excel_export.title', { defaultValue: 'Select export' })}
        srOnly
      />
      {storageSetupGate.isReady && !storageSetupGate.hasStorageRooms ? (
        <SampleNeedsStorageSetupAlert />
      ) : null}
      <div className="d-flex align-items-center justify-content-end gap-1 mb-2 excel-export-chrome">
        <CollapsibleHelpSection
          compact
          titleKey="excel_export.how_to_use_title"
          descriptionKey="excel_export.how_to_use_desc"
          stepsKey={[
            'excel_export.how_to_use_step1',
            'excel_export.how_to_use_step2',
            'excel_export.how_to_use_step3',
            'excel_export.how_to_use_step4',
          ]}
          storageKey="excel_export"
        />
        {showAdminViewToggle ? (
          <AdminStorageViewToggle
            checked={adminViewAll}
            onChange={setAdminViewAll}
            inputId="excel-export-admin-view-all"
            compact
            className="flex-shrink-0"
          />
        ) : null}
      </div>

      <p className="mb-2 excel-export-back">
        <Link
          to="/excel-import"
          className="excel-export-back-link text-decoration-none"
          aria-label={t('excel_export.back_to_import_export_short', {
            defaultValue: t('common.back', { defaultValue: 'Back' }),
          })}
          title={[
            t('excel_export.back_to_import_export', {
              defaultValue: 'Back to Excel',
            }),
            t('excel_export.room_export_hint', {
              defaultValue: 'To export a whole storage room, use Excel (I/O).',
            }),
          ].join(' · ')}
        >
          <i className="fas fa-arrow-left me-1" aria-hidden="true" />
          {t('excel_export.back_to_import_export_short', {
            defaultValue: t('common.back', { defaultValue: 'Back' }),
          })}
        </Link>
      </p>

      {success ? (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {/* 一覧スクロール中も検索・選択・出力を見失わない */}
      <Card
        ref={selectionCardRef}
        className={`mb-3 border-0 shadow-sm sample-panel-card excel-export-selection-card${
          selectedSampleIds.size > 0 ? ' excel-export-selection-card--active' : ''
        }`}
      >
        {/* md+: 見出し＋件数。xs は件数をツールバーへ（検索プレースホルダと二重にしない） */}
        <SamplePanelCardHeader
          className="d-none d-md-block"
          icon="fa-search"
          title={t('excel_export.search_aria', {
            defaultValue: 'サンプル検索',
          })}
          trailing={
            <span className="small text-muted text-nowrap">
              <strong>{selectedCountLabel}</strong>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="ms-2" aria-hidden="true" />
              ) : null}
            </span>
          }
        />
        <Card.Body className="py-2">
          <div className="excel-export-toolbar d-flex align-items-center gap-1">
            <Form.Label htmlFor="excel-export-search" className="visually-hidden">
              {t('excel_export.search_aria', { defaultValue: 'サンプル検索' })}
            </Form.Label>
            <Form.Control
              id="excel-export-search"
              type="search"
              className="min-w-0 flex-grow-1"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('excel_export.search_placeholder_short', {
                defaultValue: t('excel_export.search_placeholder', {
                  defaultValue: '名前 · 病原体で検索',
                }),
              })}
              title={t('excel_export.search_title', {
                defaultValue: t('excel_export.search_placeholder', {
                  defaultValue: '名前 · 病原体 · 保管庫などで検索',
                }),
              })}
              aria-label={t('excel_export.search_aria', {
                defaultValue: 'サンプル検索',
              })}
              autoComplete="off"
            />
            <span
              className="d-md-none small text-muted text-nowrap flex-shrink-0 excel-export-count-inline"
              title={selectedCountLabel}
            >
              <strong>{selectedCountLabel}</strong>
            </span>
            {/* xl+: 選択操作を並べる／未満: アイコンメニューに集約 */}
            <div className="d-none d-xl-flex flex-wrap gap-2 flex-shrink-0">
              {selectionActions.map((action, index) => (
                <span key={action.key} className="d-contents">
                  {index > 0 ? <TextContentSep /> : null}
                  <Button
                    variant={action.variant}
                    size="sm"
                    onClick={action.onClick}
                    disabled={action.disabled}
                    title={action.disabled ? action.disabledReason : action.title}
                    aria-label={action.label}
                  >
                    {action.busy ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        {action.label}
                      </>
                    ) : (
                      action.label
                    )}
                  </Button>
                </span>
              ))}
            </div>
            <Dropdown className="d-xl-none flex-shrink-0" align="end">
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="excel-export-selection-menu"
                className="sample-chrome-icon-btn excel-export-selection-toggle"
                disabled={selectingAllMatching}
                title={t('excel_export.selection_actions', { defaultValue: 'Selection' })}
                aria-label={t('excel_export.selection_actions_short', {
                  defaultValue: t('excel_export.selection_actions', { defaultValue: 'Select' }),
                })}
              >
                {selectingAllMatching ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fas fa-check-double" aria-hidden="true" />
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {selectionActions.map((action) => (
                  <Dropdown.Item
                    key={action.key}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    title={action.disabled ? action.disabledReason : action.title}
                  >
                    {action.title}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="primary"
              size="sm"
              className="excel-export-run-btn flex-shrink-0"
              onClick={() => void handleExport()}
              disabled={isExporting || selectedSampleIds.size === 0}
              title={
                exportDisabledReason ||
                t('excel_export.execute_export', {
                  defaultValue: '選択したサンプルを出力',
                })
              }
              aria-label={t('excel_export.execute_export_short', {
                defaultValue: '出力',
              })}
            >
              {isExporting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fas fa-file-export" aria-hidden="true" />
                  <span className="d-none d-sm-inline ms-1">
                    {t('excel_export.execute_export_short', {
                      defaultValue: '出力',
                    })}
                  </span>
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {isError && listErrorMessage ? (
        <Alert
          variant="danger"
          className="mb-3"
          dismissible
          onClose={() => {
            void refetch();
          }}
        >
          {listErrorMessage}{' '}
          <Button variant="link" className="p-0 align-baseline" onClick={() => void refetch()}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </Alert>
      ) : null}

      {isLoading && !data ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      ) : !data && isError ? null : (
        <>
          <SampleListPaginationBar
            pagination={pagination}
            listLimit={listLimit}
            onLimitChange={setListLimit}
            onPageChange={setPage}
            idPrefix="excel-export-paging-top"
            className="mb-2 d-none d-sm-flex"
          />

          <Card className="border-0 shadow-sm sample-panel-card">
            <SamplePanelCardHeader
              icon="fa-flask"
              title={t('excel_export.samples_list', { defaultValue: 'Sample list' })}
            />
            <Card.Body className="p-0 p-md-3">
              {samples.length === 0 ? (
                <Alert variant="warning" className="mb-0 m-3 m-md-0">
                  {t('excel_export.no_samples', { defaultValue: 'No samples to export' })}
                </Alert>
              ) : (
                <div className="table-responsive excel-export-table-wrap">
                  <Table hover size="sm" className="mb-0 align-middle excel-export-table">
                    <thead>
                      <tr>
                        <th className="excel-export-check-col">
                          <label className="excel-export-check-hit">
                            <input
                              ref={selectAllRef}
                              type="checkbox"
                              id="excel-export-select-page"
                              className="form-check-input"
                              aria-label={t('excel_export.select_page', {
                                defaultValue: 'Select page',
                              })}
                              checked={allPageSelected}
                              onChange={(e) => {
                                if (e.target.checked) selectPage();
                                else deselectPage();
                              }}
                            />
                          </label>
                        </th>
                        <th>{t('common.id')}</th>
                        <th className="d-none d-lg-table-cell text-nowrap">
                          {t('sample.registrant_name', { defaultValue: '作製者' })}
                        </th>
                        <th>{t('sample.sample_name', { defaultValue: 'Sample name' })}</th>
                        <th className="d-none d-md-table-cell text-nowrap">
                          {t('sample.sample_type', { defaultValue: 'Type' })}
                        </th>
                        <th className="d-none d-xl-table-cell text-nowrap">
                          {t('sample.bsl_level', { defaultValue: 'BSL level' })}
                        </th>
                        <th className="text-nowrap">
                          {t('sample.remaining_quantity', { defaultValue: 'Remaining' })}
                        </th>
                        <th className="d-none d-md-table-cell text-nowrap">
                          {t('sample.storage_room', { defaultValue: 'Storage room' })}
                        </th>
                        <th className="d-none d-xl-table-cell text-nowrap">
                          {t('common.created_at', { defaultValue: 'Registered' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map((sample) => {
                        const roomName = sample.storageRoom?.name || '-';
                        const roomDisplay = formatEntityDisplayName(roomName, t);
                        const sampleDisplay = formatEntityDisplayName(sample.name, t);
                        const { ariaLabel, title: selectTitle } = sampleSelectLabels(
                          sampleDisplay,
                          t
                        );
                        return (
                          <tr key={sample.id}>
                            <td className="excel-export-check-col">
                              <label className="excel-export-check-hit" title={selectTitle}>
                                <input
                                  type="checkbox"
                                  id={`export-sample-${sample.id}`}
                                  className="form-check-input"
                                  checked={selectedSampleIds.has(sample.id)}
                                  onChange={() => handleToggleSample(sample.id)}
                                  aria-label={ariaLabel}
                                  title={selectTitle}
                                />
                              </label>
                            </td>
                            <td>
                              {sample.id}
                              <TextContentSep />
                            </td>
                            <td className="d-none d-lg-table-cell">
                              {sample.creatorDisplayName ?? '-'}
                              <TextContentSep />
                            </td>
                            <td
                              className="excel-export-name-col"
                              title={formatEntityDisplayName(sample.name, t)}
                            >
                              <span className="excel-export-name-text">
                                {softBreakIdentifier(sample.name, t)}
                              </span>
                              <TextContentSep />
                            </td>
                            <td className="d-none d-md-table-cell">
                              {formatSampleTypeDisplay(sample.sampleType, t)}
                              <TextContentSep />
                            </td>
                            <td className="d-none d-xl-table-cell">
                              {sample.bslLevel || '-'}
                              <TextContentSep />
                            </td>
                            <td className="text-nowrap">
                              {formatSampleQuantityWithUnit(
                                sample.remainingQuantity ?? sample.initialQuantity ?? 0,
                                sample.unit,
                                t
                              )}
                              <TextContentSep />
                            </td>
                            <td className="d-none d-md-table-cell text-break" title={roomDisplay}>
                              {softBreakIdentifier(roomName, t)}
                              <TextContentSep />
                            </td>
                            <td className="d-none d-xl-table-cell">
                              {formatSampleDateTime(sample.createdAt, i18n.language)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          <SampleListPaginationBar
            pagination={pagination}
            listLimit={listLimit}
            onLimitChange={setListLimit}
            onPageChange={setPage}
            idPrefix="excel-export-paging-bottom"
            showWhenSinglePage={false}
            hideLimit
            className="mt-2"
          />
        </>
      )}
    </Container>
  );
}
